import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LegalComponent } from 'src/app/pages/legal/legal.component';
import { TranslationService } from 'src/services/translation.service';
import { normalizeWidgetLang } from 'src/services/lang.utils';

@Component({
  selector: 'belender-terms-and-policy-links',
  templateUrl: './terms-and-policy-links.component.html',
  styleUrls: ['./terms-and-policy-links.component.scss']
})
export class TermsAndPolicyLinksComponent implements OnInit, OnChanges {
  @Input() userId;
  @Input() linkType: 'both' | 'terms' | 'policy' = 'both';
  @Output('goToTerms') goTerms = new EventEmitter(null);

  @Input() firstSentence;
  @Input() secondSentence;
  @Input() thirdSentence;
  @Input() policyActionText;
  @Input() termsActionText;
  @Input() customTerms = false;
  @Input() lang = '';
  @Input() data;
  @Input() flowName;

  /** Valores por defecto del API / customization (español); si el flujo no personaliza el enlace, se usa el mapa según `lang`. */
  private static readonly defaultLinkLabelsEs = {
    a1: 'Términos y condiciones',
    a2: 'Política de privacidad',
  } as const;

  private static readonly defaultPartsEs = {
    p1: 'He leído y acepto los',
    p2: 'del servicio',
    p3: 'y la',
    a1: 'términos y condiciones',
    a2: 'política de privacidad',
  } as const;

  parts = {
    p1: 'He leído y acepto los',
    p2: 'del servicio',
    p3: 'y la',
    a1: 'términos y condiciones',
    a2: 'política de privacidad'
  };

  translatedParts = {
    es: {
      p1: 'He leído y acepto los',
      p2: 'del servicio',
      p3: 'y la',
      a1: 'términos y condiciones',
      a2: 'política de privacidad'
    },
    ca: {
      p1: 'He llegit i accepto els',
      p2: 'del servei',
      p3: 'i la',
      a1: 'termes i condicions',
      a2: 'política de privacitat'
    },
    en: {
      p1: 'I have read and accept the',
      p2: 'of the service',
      p3: 'and the',
      a1: 'terms and conditions',
      a2: 'privacy policy'
    },
    fr: {
      p1: "J'ai lu et j'accepte les",
      p2: 'du service',
      p3: 'et la',
      a1: 'conditions générales',
      a2: 'polítique de confidentialité'
    },
    it: {
      p1: 'Ho letto e accetto i',
      p2: 'del servizio',
      p3: 'e la',
      a1: 'termini e condizioni',
      a2: 'informativa sulla privacy'
    },
    pt: {
      p1: 'Li e aceito os',
      p2: 'do serviço',
      p3: 'e a',
      a1: 'termos e condições',
      a2: 'política de privacidade'
    },
    de: {
      p1: 'Ich habe die',
      p2: 'des Dienstes gelesen und akzeptiere sie',
      p3: 'sowie die',
      a1: 'Nutzungsbedingungen',
      a2: 'Datenschutzrichtlinie'
    }
  };

  policyLeadByLang = {
    es: 'He leído y acepto la',
    ca: 'He llegit i accepto la',
    en: 'I have read and accept the',
    fr: "J'ai lu et j'accepte la",
    it: 'Ho letto e accetto la',
    pt: 'Li e aceito a',
    de: 'Ich habe die'
  };

  private translatedBlock(langKey: string) {
    return this.translatedParts[langKey] ?? null;
  }

  private resolveLang(): string {
    if (this.lang) {
      return normalizeWidgetLang(this.lang);
    }
    return normalizeWidgetLang(localStorage.getItem('belender-language'));
  }

  private isSpanishDefaultText(trimmed: string, part: 'p1' | 'p2' | 'p3' | 'a1' | 'a2'): boolean {
    if (!trimmed) {
      return true;
    }
    const candidates = [
      TermsAndPolicyLinksComponent.defaultPartsEs[part],
      part === 'a1' || part === 'a2' ? TermsAndPolicyLinksComponent.defaultLinkLabelsEs[part] : null,
      this.translatedParts.es[part],
    ].filter(Boolean) as string[];
    const lower = trimmed.toLowerCase();
    return candidates.some((value) => trimmed === value || lower === value.toLowerCase());
  }

  constructor(public dialog: MatDialog, private translationService: TranslationService) { }

  ngOnInit(): void {
    this.rebuildParts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.firstSentence ||
      changes.secondSentence ||
      changes.thirdSentence ||
      changes.termsActionText ||
      changes.policyActionText ||
      changes.lang
    ) {
      this.rebuildParts();
    }
  }

  private rebuildParts(): void {
    const effectiveLang = this.resolveLang();
    this.parts = {
      p1: this.getPart(this.firstSentence, 'p1', effectiveLang),
      p2: this.getPart(this.secondSentence, 'p2', effectiveLang),
      p3: this.getPart(this.thirdSentence, 'p3', effectiveLang),
      a1: this.getPart(this.termsActionText, 'a1', effectiveLang),
      a2: this.getPart(this.policyActionText, 'a2', effectiveLang),
    };
  }

  getPart(
    sentence: string | null | undefined,
    part: 'p1' | 'p2' | 'p3' | 'a1' | 'a2',
    effectiveLang?: string
  ): string {
    const langKey = effectiveLang || this.resolveLang();
    const block = this.translatedBlock(langKey);
    const trimmed = sentence?.trim() ?? '';

    if (langKey !== 'es' && block?.[part]) {
      if (this.isSpanishDefaultText(trimmed, part)) {
        return block[part];
      }
    }

    if (trimmed) {
      return trimmed;
    }

    if (block?.[part]) {
      return block[part];
    }

    return TermsAndPolicyLinksComponent.defaultPartsEs[part] || '';
  }

  gotoTermsAndConditions(e) {
    e.preventDefault();
    e.stopPropagation();
    this.goTerms.emit(e);
  }

  gotoLegalDocument(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dialog.open(LegalComponent, {
      panelClass: 'fullscreen-dialog',
      data: { lang: this.resolveLang(), allData: this.data, flow: this.flowName }
    });
  }

  get isTermsOnly() {
    return this.linkType === 'terms';
  }

  get isPolicyOnly() {
    return this.linkType === 'policy';
  }

  get policyLeadText() {
    const trimmed = this.firstSentence?.trim() ?? '';
    const effectiveLang = this.resolveLang();

    if (trimmed) {
      const spanishLead = this.policyLeadByLang.es;
      if (effectiveLang !== 'es' && (trimmed === spanishLead || trimmed.toLowerCase() === spanishLead.toLowerCase())) {
        return this.policyLeadByLang[effectiveLang] || spanishLead;
      }
      return this.firstSentence;
    }

    return this.policyLeadByLang[effectiveLang] || this.policyLeadByLang.es;
  }
}
